/* Khizar Omer — storefront behaviour.
   Ports the React interactions (header, cart drawer, product cards, quick view,
   product page, shop filters, search) to vanilla JS on top of Shopify's Ajax APIs. */
(function () {
  'use strict';

  var KO = (window.KO = window.KO || {});
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.documentElement.classList.remove('no-js');

  /* ---------- helpers ---------- */
  function formatPKR(cents) {
    return 'PKR ' + Math.round(cents / 100).toLocaleString('en-PK');
  }
  function sized(url, w) {
    if (!url) return url;
    return url.replace(/([?&])width=\d+/, '$1width=' + w);
  }
  function lockScroll(on) {
    document.body.style.overflow = on ? 'hidden' : '';
  }
  function show(el, on) { if (el) el.hidden = !on; }

  /* Meta Pixel events — same names and fields as the original build (lib/meta-pixel.ts). */
  function newEventId(prefix) {
    var rand = window.crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    return prefix + '.' + rand;
  }
  function pixel(event, params, idPrefix) {
    if (typeof window.fbq !== 'function') return;
    window.fbq('track', event, params || {}, idPrefix ? { eventID: newEventId(idPrefix) } : undefined);
  }
  KO.pixel = pixel;
  function pixelAddToCart(t) {
    if (!t || !t.sku) return;
    pixel('AddToCart', {
      content_ids: [t.sku], content_type: 'product', content_name: t.name,
      contents: [{ id: t.sku, quantity: t.qty }], value: t.value / 100, currency: 'PKR'
    }, 'atc');
  }
  /* InitiateCheckout from the bag as Shopify sees it (works for the drawer, /cart and Buy Now). */
  function pixelInitiateCheckout() {
    if (typeof window.fbq !== 'function') return Promise.resolve();
    return fetch(KO.routes.cart + '.js').then(function (r) { return r.json(); }).then(function (cart) {
      var items = cart.items.filter(function (i) { return i.product_type !== 'Packaging'; });
      pixel('InitiateCheckout', {
        content_ids: items.map(function (i) { return i.sku; }).filter(Boolean), content_type: 'product',
        num_items: cart.item_count, value: cart.total_price / 100, currency: 'PKR'
      }, 'ic');
    }).catch(function () {});
  }

  var toastTimer;
  function toast(msg, detail) {
    var el = $('#ko-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ko-toast';
      el.setAttribute('role', 'status');
      el.className = 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] bg-background border border-hairline shadow-[0_10px_30px_-10px_hsl(var(--ink)/0.35)] px-5 py-4 text-[13px] text-ink max-w-[90vw]';
      document.body.appendChild(el);
    }
    el.innerHTML = '<p class="font-medium"></p><p class="ko-micro mt-1"></p>';
    el.children[0].textContent = msg;
    el.children[1].textContent = detail || '';
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3500);
  }

  /* ---------- reveal ---------- */
  function initReveal(root) {
    var els = $$('[data-ko-reveal]:not(.is-in)', root);
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.style.transitionDelay = (el.getAttribute('data-delay') || 0) + 'ms';
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- header ---------- */
  function initHeader() {
    var header = $('[data-ko-header]');
    if (!header) return;
    var bar = $('[data-ko-header-bar]', header);
    var mark = $('[data-ko-wordmark]', header);
    var transparent = header.getAttribute('data-transparent') === 'true';
    var count = $('[data-ko-cart-count]', header);
    var BASE = 'fixed left-0 right-0 z-40 transition-[top,background-color,box-shadow] duration-500 ';
    var STATES = {
      top_transparent: 'top-[var(--ann-h,36px)] bg-transparent',
      top_solid: 'top-[var(--ann-h,36px)] bg-background md:bg-background/95 md:backdrop-blur-md border-b border-hairline',
      scrolled: 'top-0 bg-background md:bg-background/90 md:backdrop-blur-lg border-b border-hairline shadow-[0_1px_20px_-8px_hsl(var(--ink)/0.15)]'
    };
    var BAR = 'flex items-center justify-between gap-6 transition-[height,padding] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ';
    var BAR_TOP = 'h-16 pt-1 md:h-[5.5rem] md:pt-4 lg:h-[6.5rem] lg:pt-6';
    var BAR_SCROLLED = 'h-11 md:h-[3.25rem]';
    var MARK = 'w-auto max-w-[46vw] sm:max-w-[52vw] object-contain transition-[height,transform,filter] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[height,transform] ';
    var MARK_TOP = 'h-6 sm:h-7 md:h-8';
    var MARK_SCROLLED = 'h-5 sm:h-6 md:h-7';
    var last = null;
    function apply() {
      var scrolled = window.scrollY > 20;
      if (scrolled === last) return;
      last = scrolled;
      var solid = !transparent || scrolled;
      header.className = BASE + (scrolled ? STATES.scrolled : transparent ? STATES.top_transparent : STATES.top_solid);
      header.setAttribute('data-solid', solid ? 'true' : 'false');
      if (bar) bar.className = BAR + (scrolled ? BAR_SCROLLED : BAR_TOP);
      if (mark) {
        mark.className = MARK + (scrolled ? MARK_SCROLLED : MARK_TOP);
        mark.style.filter = solid ? '' : 'invert(1)';
      }
      $$('.ko-nav-link', header).forEach(function (a) { a.style.color = solid ? '' : 'hsl(var(--background))'; });
      if (count) count.className = 'absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] font-medium tracking-normal rounded-full ' + (solid ? 'bg-ink text-background' : 'bg-background text-ink');
    }
    apply();
    window.addEventListener('scroll', apply, { passive: true });
  }

  /* ---------- hero scroll fade + parallax ---------- */
  function initScrollEffects() {
    if (reduceMotion || !canHover) return;
    var hero = $('[data-ko-hero]');
    var copy = hero && $('[data-hero-copy]', hero);
    var pars = $$('[data-ko-parallax]');
    if (!copy && !pars.length) return;
    var ticking = false;
    function frame() {
      ticking = false;
      var vh = window.innerHeight;
      if (copy) {
        var r = hero.getBoundingClientRect();
        var p = Math.min(1, Math.max(0, -r.top / Math.max(1, r.height)));
        copy.style.transform = 'translate3d(0,' + (-70 * p) + 'px,0)';
        copy.style.opacity = String(1 - 0.75 * p);
      }
      pars.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-ko-parallax')) || 40;
        var r = el.getBoundingClientRect();
        var total = vh + r.height;
        var prog = Math.min(1, Math.max(0, (vh - r.top) / total));
        el.style.transform = 'translate3d(0,' + (-speed / 2 + speed * prog) + 'px,0)';
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
    setTimeout(function () { frame(); window.addEventListener('scroll', onScroll, { passive: true }); }, copy ? 1200 : 0);
  }

  /* ---------- mobile menu ---------- */
  function initMenu() {
    var menu = $('[data-ko-mobile-menu]');
    if (!menu) return;
    $$('[data-ko-menu-open]').forEach(function (b) { b.addEventListener('click', function () { menu.hidden = false; lockScroll(true); }); });
    $$('[data-ko-menu-close], a', menu).forEach(function (b) {
      b.addEventListener('click', function () { menu.hidden = true; lockScroll(false); });
    });
  }

  /* ---------- search overlay ---------- */
  function initSearch() {
    var ov = $('[data-ko-search]');
    if (!ov) return;
    var input = $('[data-ko-search-input]', ov);
    var popular = $('[data-ko-search-popular]', ov);
    var results = $('[data-ko-search-results]', ov);
    var list = $('[data-ko-search-list]', ov);
    var countEl = $('[data-ko-search-count]', ov);
    var empty = $('[data-ko-search-empty]', ov);
    var timer, seq = 0;
    function open() { ov.hidden = false; lockScroll(true); setTimeout(function () { input.focus(); }, 30); }
    function close() { ov.hidden = true; lockScroll(false); input.value = ''; run(); }
    function run() {
      var q = input.value.trim();
      show(popular, !q);
      show(results, !!q);
      if (!q) return;
      var my = ++seq;
      var url = KO.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&section_id=ko-predictive-search';
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        if (my !== seq) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrap = doc.querySelector('[data-ko-ps-count]');
        var n = wrap ? Math.min(6, parseInt(wrap.getAttribute('data-ko-ps-count'), 10) || 0) : 0;
        countEl.textContent = n + ' result' + (n === 1 ? '' : 's');
        show(empty, n === 0);
        list.innerHTML = wrap ? wrap.innerHTML : '';
        $$('a', list).forEach(function (a) { a.addEventListener('click', function () { lockScroll(false); }); });
      }).catch(function () {});
    }
    $$('[data-ko-search-open]').forEach(function (b) { b.addEventListener('click', open); });
    $$('[data-ko-search-close]', ov).forEach(function (b) { b.addEventListener('click', close); });
    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(run, 160); });
    $$('[data-ko-search-term]', ov).forEach(function (b) {
      b.addEventListener('click', function () { input.value = b.getAttribute('data-ko-search-term'); run(); input.focus(); });
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !ov.hidden) close(); });
  }

  /* ---------- cart ---------- */
  var Cart = (KO.cart = {
    drawer: null,
    busy: false,
    isOpen: function () { return this.drawer && !this.drawer.hidden; },
    open: function () {
      if (!this.drawer) { window.location.href = KO.routes.cart; return; }
      this.drawer.hidden = false;
      lockScroll(true);
    },
    close: function () { if (this.drawer) { this.drawer.hidden = true; lockScroll(false); } },
    setLoading: function (on) {
      this.busy = on;
      var body = this.drawer && $('[data-ko-cart-body]', this.drawer);
      if (body) { body.classList.toggle('opacity-60', on); body.classList.toggle('opacity-100', !on); }
      var btn = this.drawer && $('[data-ko-checkout]', this.drawer);
      if (btn) {
        btn.disabled = on;
        btn.innerHTML = on ? '<svg class="lucide w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Updating your bag…' : 'Secure Checkout';
      }
      $$('[data-ko-qty]', this.drawer || document).forEach(function (b) { b.disabled = on; });
    },
    render: function (sectionsHtml, cartState) {
      if (sectionsHtml && sectionsHtml['ko-cart-drawer'] && this.drawer) {
        var doc = new DOMParser().parseFromString(sectionsHtml['ko-cart-drawer'], 'text/html');
        var fresh = doc.querySelector('[data-ko-cart-inner]');
        var cur = $('[data-ko-cart-inner]', this.drawer);
        if (fresh && cur) {
          fresh.classList.remove('animate-drawer-in');
          cur.replaceWith(fresh);
        }
      }
      if (cartState) this.updateCount(cartState.item_count);
      bindCartControls(this.drawer);
      if ($('[data-ko-cart-page]') && !this._reloading) { this._reloading = true; window.location.reload(); }
    },
    updateCount: function (n) {
      $$('[data-ko-cart-count]').forEach(function (el) { el.textContent = n; el.hidden = !n; });
      var openBtn = $('[data-ko-cart-open]');
      if (openBtn) openBtn.setAttribute('aria-label', 'Cart (' + n + ')');
    },
    post: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (j) { if (!r.ok) throw j; return j; });
      });
    },
    /* Re-render the drawer from a sections payload, reconciling the free box line first. */
    settle: function (html, depth) {
      var self = this;
      var doc = html ? new DOMParser().parseFromString(html, 'text/html') : document;
      var updates = self.boxUpdates(doc);
      if (updates && !depth) {
        return self.post(KO.routes.cartUpdate + '.js', { updates: updates, sections: 'ko-cart-drawer' })
          .then(function (res) { return self.settle(res.sections && res.sections['ko-cart-drawer'], 1).then(function () { self.updateCount(res.item_count); }); })
          .catch(function () { return self.renderHtml(html); });
      }
      return Promise.resolve(self.renderHtml(html));
    },
    renderHtml: function (html) {
      if (!html) return;
      this.render({ 'ko-cart-drawer': html });
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var h = doc.querySelector('[data-ko-cart-inner] h2');
      var m = h && h.textContent.match(/\((\d+)\)/);
      if (m) this.updateCount(parseInt(m[1], 10));
    },
    /* Products with a box included always ship with the free box line (qty = eligible units);
       a paid box is dropped once no fabric is left in the bag. Eligibility comes from the
       server-rendered lines (data-gift-included), so it works on every page. */
    boxUpdates: function (doc) {
      var gb = KO.giftBox || {};
      if (!gb.freeVariantId && !gb.paidVariantId) return null;
      var eligible = 0, freeQty = 0, paidQty = 0, products = 0;
      var lines = doc.querySelectorAll('[data-ko-cart-drawer] [data-ko-line], [data-ko-cart-inner] [data-ko-line]');
      var seen = [];
      Array.prototype.forEach.call(lines, function (li) {
        if (seen.indexOf(li.getAttribute('data-ko-line')) > -1) return;
        seen.push(li.getAttribute('data-ko-line'));
        var vid = parseInt(li.getAttribute('data-ko-line-variant'), 10);
        var q = parseInt(li.getAttribute('data-ko-line-qty'), 10) || 0;
        if (vid === gb.freeVariantId) { freeQty += q; return; }
        if (vid === gb.paidVariantId) { paidQty += q; return; }
        if (li.getAttribute('data-ko-line-box') === 'true') return;
        products += q;
        if (li.getAttribute('data-gift-included') === '1') eligible += q;
      });
      var updates = {};
      if (gb.freeVariantId && freeQty !== eligible) updates[gb.freeVariantId] = eligible;
      if (gb.paidVariantId && paidQty > 0 && products === 0) updates[gb.paidVariantId] = 0;
      return Object.keys(updates).length ? updates : null;
    },
    refresh: function () {
      var self = this;
      return fetch(KO.routes.root + '?sections=ko-cart-drawer').then(function (r) { return r.json(); })
        .then(function (secs) { return self.settle(secs['ko-cart-drawer']); });
    },
    add: function (items, opts) {
      var self = this;
      opts = opts || {};
      if (opts.openDrawer !== false) self.open();
      self.setLoading(true);
      return self.post(KO.routes.cartAdd + '.js', { items: items, sections: 'ko-cart-drawer' })
        .then(function (res) { pixelAddToCart(opts.track); return self.settle(res.sections && res.sections['ko-cart-drawer']).then(function () { return res; }); })
        .catch(function (err) {
          toast("Couldn't add to bag. Please try again.", err && err.description ? err.description : '');
          return self.refresh().then(function () { return null; });
        })
        .finally(function () { self.setLoading(false); });
    },
    change: function (line, qty) {
      var self = this;
      self.setLoading(true);
      return self.post(KO.routes.cartChange + '.js', { line: line, quantity: Math.max(0, qty), sections: 'ko-cart-drawer' })
        .then(function (res) { return self.settle(res.sections && res.sections['ko-cart-drawer']); })
        .catch(function () { toast("Couldn't update your bag."); return self.refresh(); })
        .finally(function () { self.setLoading(false); });
    }
  });

  function bindCartControls(root) {
    if (!root) return;
    $$('[data-ko-line]', root).forEach(function (li) {
      var line = parseInt(li.getAttribute('data-ko-line'), 10);
      $$('[data-ko-qty]', li).forEach(function (b) {
        if (b._ko) return; b._ko = true;
        b.addEventListener('click', function (e) {
          e.preventDefault();
          if (Cart.busy) return;
          Cart.change(line, parseInt(b.getAttribute('data-ko-qty'), 10));
        });
      });
    });
    $$('[data-ko-cart-close]', root).forEach(function (b) {
      if (b._ko) return; b._ko = true;
      b.addEventListener('click', function () { Cart.close(); });
    });
    $$('[data-ko-checkout]', root).forEach(function (b) {
      if (b._ko) return; b._ko = true;
      b.addEventListener('click', function (e) {
        var lines = $$('[data-ko-line]', root);
        if (lines.length && lines.every(function (l) { return l.getAttribute('data-ko-line-box') === 'true'; })) {
          e.preventDefault();
          toast("Packaging can't be ordered on its own", 'Please add a piece to your bag before checking out.');
          return;
        }
        if (typeof window.fbq === 'function' && b.form) {
          e.preventDefault();
          var form = b.form;
          var go = function () { var h = document.createElement('input'); h.type = 'hidden'; h.name = 'checkout'; form.appendChild(h); form.submit(); };
          var timer = setTimeout(go, 1500);
          pixelInitiateCheckout().then(function () { clearTimeout(timer); go(); });
        }
      });
    });
    bindUpsell(root);
  }

  function bindUpsell(root) {
    $$('[data-ko-upsell-add]', root).forEach(function (b) {
      if (b._ko) return; b._ko = true;
      b.addEventListener('click', function () {
        var id = parseInt(b.getAttribute('data-ko-upsell-add'), 10);
        var row = b.closest('[data-ko-upsell-row]');
        var st = function (s) { $$('[data-ko-state]', b).forEach(function (x) { x.hidden = x.getAttribute('data-ko-state') !== s; }); };
        st('pending'); b.disabled = true;
        var inDrawer = Cart.drawer && Cart.drawer.contains(b);
        var track = { sku: b.getAttribute('data-sku'), name: b.getAttribute('data-name'), value: parseInt(b.getAttribute('data-price'), 10) || 0, qty: 1 };
        Cart.add([{ id: id, quantity: 1 }], { openDrawer: !inDrawer, track: track }).then(function (ok) {
          if (!ok) { st('idle'); b.disabled = false; return; }
          if (!document.body.contains(b)) return;
          st('added'); b.disabled = false;
          b.classList.add('bg-ink', 'border-ink', 'text-background');
          if (row) { row.classList.remove('border-hairline'); row.classList.add('border-gold'); var n = $('[data-ko-upsell-note]', row); if (n) n.textContent = 'Added to your bag'; }
        });
      });
    });
  }

  function initCart() {
    Cart.drawer = $('[data-ko-cart-drawer]');
    bindCartControls(Cart.drawer);
    var page = $('[data-ko-cart-page]');
    if (page) bindCartControls(page);
    $$('[data-ko-cart-open]').forEach(function (a) {
      a.addEventListener('click', function (e) { if (Cart.drawer) { e.preventDefault(); Cart.open(); } });
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && Cart.isOpen()) Cart.close(); });
    // The free box line follows the bag on every page load too (e.g. after editing at /cart).
    if (Cart.boxUpdates(document)) Cart.refresh();
  }

  /* ---------- product data helpers ---------- */
  function parseJSON(el) {
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function registerProduct(p) {
    if (!p) return p;
    KO.giftIncludedHandles = KO.giftIncludedHandles || [];
    if (p.giftBoxIncluded && KO.giftIncludedHandles.indexOf(p.handle) === -1) KO.giftIncludedHandles.push(p.handle);
    return p;
  }
  function defaultColor(p) {
    var pref = null;
    p.colors.forEach(function (c) { if (p.cardSku && c.sku === p.cardSku) pref = c; });
    pref = pref || p.colors[0];
    if (pref && !pref.available) {
      var avail = p.colors.filter(function (c) { return c.available; })[0];
      if (avail) pref = avail;
    }
    return pref;
  }
  function swatchBg(c) { return c.images && c.images[0] ? c.images[0] : null; }
  function salePct(price, compare) { return compare && compare > price ? Math.round(((compare - price) / compare) * 100) : 0; }

  /* ---------- product cards ---------- */
  function initCards(root) {
    $$('[data-ko-card]', root).forEach(function (card) {
      if (card._ko) return; card._ko = true;
      var p = registerProduct(parseJSON($('[data-ko-card-json]', card)));
      if (!p || !p.colors.length) return;
      var active = defaultColor(p);
      var idx = 0;
      var primaryImg = $('[data-ko-card-img]', card);
      var secondary = $('[data-ko-card-secondary]', card);
      var secondaryImg = secondary && $('img', secondary);
      var primaryWrap = $('[data-ko-card-primary]', card);
      var dots = $('[data-ko-card-dots]', card);
      var prev = $('[data-ko-card-prev]', card), next = $('[data-ko-card-next]', card);
      var swatches = $$('[data-ko-swatch]', card);
      var low = $('[data-ko-card-low]', card);
      var soldline = $('[data-ko-card-soldline]', card);
      var badges = $('[data-ko-card-badges]', card);
      var addWrap = $('[data-ko-card-add-wrap]', card);
      var warm = false;

      function gallery() {
        var g = (active.images || []).slice();
        if (p.lead > 0 && g.length > p.lead) g = g.slice(p.lead).concat(g.slice(0, p.lead));
        return g;
      }
      function href() { return p.url + '?variant=' + active.variantId; }
      function render() {
        var g = gallery();
        var safe = g.length ? Math.min(idx, g.length - 1) : 0;
        if (primaryImg && g[safe]) primaryImg.src = sized(g[safe], 800);
        var multi = g.length > 1;
        if (primaryWrap) primaryWrap.classList.toggle('has-secondary', multi);
        if (secondary) {
          secondary.hidden = !(multi && warm);
          if (multi && warm && secondaryImg) secondaryImg.src = sized(g[(safe + 1) % g.length], 800);
        }
        [prev, next].forEach(function (b) { if (b) b.style.display = multi ? '' : 'none'; });
        if (dots) {
          dots.innerHTML = multi ? g.map(function (_, i) {
            return '<span class="w-1.5 h-1.5 rounded-full transition-colors ' + (i === safe ? 'bg-ink' : 'bg-ink/25') + '"></span>';
          }).join('') : '';
        }
        var sold = !active.available;
        if (addWrap) addWrap.hidden = sold;
        if (low) {
          var n = active.inventory;
          var isLow = !sold && typeof n === 'number' && n > 0 && n <= 5;
          low.hidden = !isLow;
          if (isLow) low.textContent = 'Only ' + n + ' left' + (active.name ? ' in ' + active.name : '');
        }
        if (soldline) { soldline.hidden = !sold; soldline.textContent = active.name + ' — Sold Out'; }
        if (badges) {
          var cls = 'max-w-full whitespace-nowrap px-2 py-0.5 md:px-2.5 md:py-1';
          var pct = salePct(p.price, p.compareAt);
          var html = '';
          if (sold) html = '<span class="ko-tag-out ' + cls + '">Sold Out</span>';
          else if (pct > 0) html = '<span class="ko-tag-sale ' + cls + '">' + (p.saleLabel || pct + '% Off') + '</span>';
          else {
            if (p.isNew) html += '<span class="ko-tag-new bg-background/90 ' + cls + '">' + (p.newLabel || 'New Arrival') + '</span>';
            if (p.limited) html += '<span class="ko-tag-limited ' + cls + '">Limited Edition</span>';
          }
          badges.innerHTML = html;
          badges.hidden = !html;
        }
        $$('[data-ko-card-link]', card).forEach(function (a) { a.href = href(); });
        swatches.forEach(function (s) {
          var c = p.colors[parseInt(s.getAttribute('data-ko-swatch'), 10)];
          var on = c === active;
          s.setAttribute('aria-pressed', on ? 'true' : 'false');
          s.classList.toggle('border-ink', on);
          s.classList.toggle('ring-1', on);
          s.classList.toggle('ring-ink', on);
          s.classList.toggle('ring-offset-2', on);
          s.classList.toggle('ring-offset-background', on);
          s.classList.toggle('border-hairline', !on);
          s.classList.toggle('hover:border-ink', !on);
        });
      }
      swatches.forEach(function (s) {
        var c = p.colors[parseInt(s.getAttribute('data-ko-swatch'), 10)];
        var img = $('img', s);
        if (img && swatchBg(c)) { img.src = sized(swatchBg(c), 120); img.hidden = false; }
        s.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          active = c; idx = 0; render();
        });
      });
      function go(e, dir) {
        e.preventDefault(); e.stopPropagation();
        var g = gallery(); if (!g.length) return;
        idx = (Math.min(idx, g.length - 1) + dir + g.length) % g.length;
        render();
      }
      if (prev) prev.addEventListener('click', function (e) { go(e, -1); });
      if (next) next.addEventListener('click', function (e) { go(e, 1); });
      function warmUp() { if (!warm) { warm = true; render(); } }
      card.addEventListener('pointerenter', warmUp);
      card.addEventListener('touchstart', warmUp, { passive: true });

      $$('[data-ko-card-add]', card).forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          Cart.add([{ id: active.variantId, quantity: 1 }], { track: { sku: active.sku, name: p.title, value: active.price, qty: 1 } });
          $$('[data-ko-card-add]', card).forEach(function (x) {
            var d = $('[data-ko-label-default]', x), a = $('[data-ko-label-added]', x);
            if (d) d.classList.add('hidden');
            if (a) { a.classList.remove('hidden'); a.classList.add('inline-flex'); }
          });
          setTimeout(function () {
            $$('[data-ko-card-add]', card).forEach(function (x) {
              var d = $('[data-ko-label-default]', x), a = $('[data-ko-label-added]', x);
              if (d) d.classList.remove('hidden');
              if (a) { a.classList.add('hidden'); a.classList.remove('inline-flex'); }
            });
          }, 1400);
        });
      });
      var qv = $('[data-ko-quick-view]', card);
      if (qv) qv.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); QuickView.open(p, active); });
      render();
    });
  }

  /* ---------- quick view ---------- */
  var QuickView = {
    el: null, p: null, color: null, qty: 1,
    init: function () {
      this.el = $('[data-ko-qv]');
      if (!this.el) return;
      var self = this;
      $$('[data-ko-qv-close]', this.el).forEach(function (b) { b.addEventListener('click', function () { self.close(); }); });
      $('[data-qv="minus"]', this.el).addEventListener('click', function () { self.qty = Math.max(1, self.qty - 1); self.render(); });
      $('[data-qv="plus"]', this.el).addEventListener('click', function () { self.qty += 1; self.render(); });
      $('[data-qv="add"]', this.el).addEventListener('click', function () {
        if (!self.color.available) return;
        var items = [{ id: self.color.variantId, quantity: self.qty }];
        var track = { sku: self.color.sku, name: self.p.title, value: self.color.price * self.qty, qty: self.qty };
        self.close();
        Cart.add(items, { track: track });
      });
      $('[data-qv="link"]', this.el).addEventListener('click', function () { lockScroll(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.el && !self.el.hidden) self.close(); });
    },
    open: function (p, color) {
      if (!this.el) return;
      this.p = p; this.color = color || defaultColor(p); this.qty = 1;
      this.el.setAttribute('aria-label', p.title + ' — quick view');
      this.render();
      this.el.hidden = false;
      lockScroll(true);
    },
    close: function () { if (this.el) { this.el.hidden = true; lockScroll(false); } },
    render: function () {
      var p = this.p, c = this.color, el = this.el, self = this;
      var q = function (k) { return $('[data-qv="' + k + '"]', el); };
      var img = q('img');
      if (c.images && c.images[0]) { img.src = sized(c.images[0], 1200); img.hidden = false; } else { img.hidden = true; }
      img.alt = p.title;
      show(q('soldout'), !c.available);
      q('fabric').textContent = p.fabric;
      q('title').textContent = p.title;
      q('price').textContent = formatPKR(p.price);
      var compare = q('compare'), sale = q('sale');
      show(compare, !!p.compareAt); if (p.compareAt) compare.textContent = formatPKR(p.compareAt);
      var pct = salePct(p.price, p.compareAt);
      show(sale, pct > 0); if (pct > 0) sale.textContent = p.saleLabel || pct + '% Off';
      q('colorname').textContent = c.name;
      var sw = q('swatches');
      sw.innerHTML = '';
      p.colors.forEach(function (col) {
        var b = document.createElement('button');
        b.type = 'button'; b.title = col.name; b.setAttribute('aria-label', col.name);
        b.setAttribute('aria-pressed', col === c ? 'true' : 'false');
        b.className = 'w-9 h-9 border transition-all ' + (col === c ? 'border-ink ring-1 ring-ink ring-offset-2 ring-offset-background' : 'border-hairline hover:border-ink');
        b.style.background = col.hex;
        var bg = swatchBg(col);
        if (bg) { b.style.backgroundImage = 'url(' + sized(bg, 120) + ')'; b.style.backgroundSize = 'cover'; b.style.backgroundPosition = 'center'; }
        b.addEventListener('click', function () { self.color = col; self.render(); });
        sw.appendChild(b);
      });
      q('qty').textContent = this.qty;
      var add = q('add');
      add.textContent = c.available ? 'Add to Cart' : 'Sold Out';
      add.disabled = !c.available;
      add.classList.toggle('opacity-40', !c.available);
      add.classList.toggle('cursor-not-allowed', !c.available);
      q('link').href = p.url + '?variant=' + c.variantId;
      var rows = [['Fabric', p.fabric], ['Suit Length', p.suitLength]];
      if (p.season) rows.push(['Season', p.season]);
      rows.push(['Care', p.care]);
      q('specs').innerHTML = rows.map(function (r, i) {
        return '<div class="flex justify-between py-3 ' + (i < rows.length - 1 ? 'ko-hairline-b' : '') + '"><dt class="ko-micro"></dt><dd class="text-[13px] text-ink"></dd></div>';
      }).join('');
      $$('dt', q('specs')).forEach(function (dt, i) { dt.textContent = rows[i][0]; dt.nextSibling.textContent = rows[i][1] || ''; });
    }
  };

  /* ---------- product page ---------- */
  function initProduct() {
    var root = $('[data-ko-product]');
    if (!root) return;
    var p = registerProduct(parseJSON($('[data-ko-product-json]', root)));
    if (!p) return;
    var currentId = parseInt(($('[data-ko-current]', root) || {}).textContent, 10);
    var color = p.colors.filter(function (c) { return c.variantId === currentId; })[0] || defaultColor(p);
    var params = new URLSearchParams(location.search);
    if (!params.get('variant')) color = defaultColor(p);
    var canBox = root.getAttribute('data-can-box') === 'true';
    var boxPrice = parseInt(root.getAttribute('data-gift-price'), 10) || 0;
    var threshold = parseInt(root.getAttribute('data-threshold'), 10) || KO.freeShippingThreshold;
    var withBox = false, qty = 1, imgIndex = 0;

    var thumbs = $('[data-ko-thumbs]', root);
    var mainImg = $('[data-ko-main-img]', root);
    var zoom = $('[data-ko-zoom]', root);
    var nav = $('[data-ko-gal-nav]', root);
    var countEl = $('[data-ko-gal-count]', root);
    var swatches = $('[data-ko-swatches]', root);
    var lightbox = $('[data-ko-lightbox]', root);

    function imgs() { return (color.images || []).filter(function (v, i, a) { return v && a.indexOf(v) === i; }); }

    function renderGallery() {
      var g = imgs();
      imgIndex = Math.min(imgIndex, Math.max(0, g.length - 1));
      if (g[imgIndex]) mainImg.src = sized(g[imgIndex], 1400);
      if (thumbs) {
        thumbs.innerHTML = '';
        g.forEach(function (src, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'w-16 lg:w-20 aspect-[4/5] overflow-hidden ' + (i === imgIndex ? 'outline outline-1 outline-ink' : 'outline outline-1 outline-hairline');
          b.innerHTML = '<img src="' + sized(src, 200) + '" alt="" class="w-full h-full object-cover" loading="lazy">';
          b.addEventListener('click', function () { imgIndex = i; renderGallery(); });
          thumbs.appendChild(b);
        });
      }
      show(nav, g.length > 1 || p.colors.length > 1);
      if (countEl) countEl.textContent = (imgIndex + 1) + ' / ' + g.length;
    }

    function move(dir) {
      var g = imgs();
      var n = imgIndex + dir;
      if (n >= 0 && n < g.length) { imgIndex = n; renderGallery(); return; }
      var ci = p.colors.indexOf(color);
      var nc = p.colors[(Math.max(0, ci) + dir + p.colors.length) % p.colors.length];
      if (!nc || nc === color) { imgIndex = dir === 1 ? 0 : g.length - 1; renderGallery(); return; }
      var count = (nc.images || []).length;
      select(nc, dir === 1 ? 0 : Math.max(0, count - 1));
    }

    function renderInfo() {
      var sold = !color.available;
      var unit = color.price + (canBox && withBox ? boxPrice : 0);
      $$('[data-ko-unit-total]', root).forEach(function (el) { el.textContent = formatPKR(unit); });
      show($('[data-ko-soldout-tag]', root), sold);
      var cmp = $('[data-ko-compare]', root);
      if (cmp) { show(cmp, !!color.compareAt); if (color.compareAt) cmp.textContent = formatPKR(color.compareAt); }
      var sale = $('[data-ko-sale]', root);
      var pct = salePct(color.price, color.compareAt);
      if (sale) { show(sale, pct > 0 && !sold); sale.textContent = sale.getAttribute('data-label') || ('Sale — ' + pct + '% Off'); }
      show($('[data-ko-incl-box]', root), canBox && withBox);
      $$('[data-ko-hide-soldout]', root).forEach(function (el) { show(el, !sold); });
      $$('[data-ko-color-name]', root).forEach(function (el) { el.textContent = color.name; });
      var sku = $('[data-ko-sku]', root); if (sku) sku.textContent = color.sku || '';
      var n = color.inventory;
      var stats = $('[data-ko-livestats]', root);
      if (stats) { var on = typeof n === 'number' && n > 0 && n <= 12; show(stats, on); if (on) $('[data-ko-stock-n]', stats).textContent = n; }
      var low = $('[data-ko-lowstock]', root);
      if (low) { var isLow = !sold && typeof n === 'number' && n > 0 && n <= 5; show(low, isLow); if (isLow) low.textContent = 'Only ' + n + ' left in ' + color.name; }
      $$('[data-ko-add]', root).forEach(function (b) {
        b.disabled = sold;
        b.textContent = sold ? 'Sold Out' : 'Add to Cart';
        b.classList.toggle('opacity-40', sold);
        b.classList.toggle('cursor-not-allowed', sold);
      });
      $$('[data-ko-buy]', root).forEach(function (b) {
        b.disabled = sold;
        if (b.classList.contains('ko-btn-primary')) { b.classList.toggle('opacity-40', sold); b.classList.toggle('cursor-not-allowed', sold); }
      });
      $('[data-ko-qty]', root).textContent = qty;
      var ship = $('[data-ko-ship-note]', root);
      if (ship) {
        var total = color.price * qty + (canBox && withBox ? boxPrice * qty : 0);
        ship.textContent = total >= threshold ? 'This order qualifies for free delivery.' : 'Free delivery on orders over ' + formatPKR(threshold) + '.';
      }
      // packaging
      $$('[data-ko-box-set]', root).forEach(function (b) {
        var v = b.getAttribute('data-ko-box-set') === '1';
        var on = v === withBox;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('bg-ink', on); b.classList.toggle('text-background', on); b.classList.toggle('border-ink', on);
        b.classList.toggle('border-hairline', !on); b.classList.toggle('text-ink', !on);
      });
      var tog = $('[data-ko-box-toggle]', root);
      if (tog) {
        tog.setAttribute('aria-pressed', withBox ? 'true' : 'false');
        ['border-ink', 'bg-ink/[0.04]', 'shadow-[0_1px_0_0_hsl(var(--ink))]'].forEach(function (c) { tog.classList.toggle(c, withBox); });
        ['border-hairline', 'hover:border-ink/60'].forEach(function (c) { tog.classList.toggle(c, !withBox); });
        var chk = $('[data-ko-box-check]', tog);
        ['bg-ink', 'border-ink'].forEach(function (c) { chk.classList.toggle(c, withBox); });
        ['border-ink/30', 'group-hover:border-ink/60'].forEach(function (c) { chk.classList.toggle(c, !withBox); });
        show($('[data-ko-box-tick]', tog), withBox);
      }
    }

    function renderSwatches() {
      swatches.innerHTML = '';
      p.colors.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        var on = c === color;
        b.setAttribute('aria-label', c.name + (c.available ? '' : ' (sold out)'));
        b.title = c.available ? c.name : c.name + ' — Sold Out';
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.className = 'relative w-11 h-11 rounded-full border overflow-hidden transition-all duration-150 active:scale-90 ' +
          (on ? 'border-ink ring-1 ring-ink ring-offset-2 ring-offset-background' : 'border-hairline hover:border-ink') + ' ' + (c.available ? '' : 'opacity-45');
        b.style.background = c.hex;
        var bg = swatchBg(c);
        if (bg) { b.style.backgroundImage = 'url(' + sized(bg, 160) + ')'; b.style.backgroundSize = 'cover'; b.style.backgroundPosition = 'center'; }
        if (!c.available) {
          b.innerHTML = '<span aria-hidden="true" class="absolute inset-0 rounded-full bg-background/45" style="background-image:linear-gradient(to top left, transparent calc(50% - 0.5px), currentColor calc(50% - 0.5px), currentColor calc(50% + 0.5px), transparent calc(50% + 0.5px))"></span>';
        }
        b.addEventListener('click', function () { if (c !== color) select(c, 0); });
        swatches.appendChild(b);
      });
    }

    function select(c, index) {
      color = c; imgIndex = index || 0;
      var u = new URL(location.href);
      u.searchParams.set('variant', c.variantId);
      history.replaceState(null, '', u.toString());
      renderSwatches(); renderGallery(); renderInfo();
    }

    // zoom
    var isTouch = window.matchMedia('(hover: none)').matches;
    zoom.addEventListener('mouseenter', function () { if (!isTouch) mainImg.style.transform = 'scale(2.2)'; });
    zoom.addEventListener('mouseleave', function () { mainImg.style.transform = 'scale(1)'; });
    zoom.addEventListener('mousemove', function (e) {
      var r = zoom.getBoundingClientRect();
      mainImg.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
    });
    zoom.addEventListener('click', function () {
      if (!isTouch || !lightbox) return;
      $('[data-ko-lightbox-img]', lightbox).src = sized(imgs()[imgIndex], 2000);
      lightbox.hidden = false; lockScroll(true);
    });
    if (lightbox) $$('[data-ko-lightbox-close]', lightbox).forEach(function (b) { b.addEventListener('click', function () { lightbox.hidden = true; lockScroll(false); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && lightbox && !lightbox.hidden) { lightbox.hidden = true; lockScroll(false); } });

    var prevB = $('[data-ko-gal-prev]', root), nextB = $('[data-ko-gal-next]', root);
    if (prevB) prevB.addEventListener('click', function () { move(-1); });
    if (nextB) nextB.addEventListener('click', function () { move(1); });

    $('[data-ko-qty-minus]', root).addEventListener('click', function () { qty = Math.max(1, qty - 1); renderInfo(); });
    $('[data-ko-qty-plus]', root).addEventListener('click', function () { qty += 1; renderInfo(); });
    $$('[data-ko-box-set]', root).forEach(function (b) { b.addEventListener('click', function () { withBox = b.getAttribute('data-ko-box-set') === '1'; renderInfo(); }); });
    var tog = $('[data-ko-box-toggle]', root);
    if (tog) tog.addEventListener('click', function () { withBox = !withBox; renderInfo(); });

    function trackInfo() { return { sku: color.sku, name: p.title, value: color.price * qty, qty: qty }; }
    function items() {
      var list = [{ id: color.variantId, quantity: qty }];
      if (canBox && withBox && KO.giftBox && KO.giftBox.paidVariantId) list.push({ id: KO.giftBox.paidVariantId, quantity: qty });
      return list;
    }
    $$('[data-ko-add]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        if (!color.available) return;
        $$('[data-ko-add]', root).forEach(function (x) { if (x.classList.contains('ko-btn-full')) x.textContent = 'Adding…'; });
        Cart.add(items(), { track: trackInfo() }).then(function () { renderInfo(); });
      });
    });
    $$('[data-ko-buy]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        if (!color.available) return;
        Cart.add(items(), { openDrawer: false, track: trackInfo() }).then(function (ok) {
          if (!ok) return;
          pixelInitiateCheckout().then(function () { window.location.href = '/checkout'; });
        });
      });
    });

    document.body.setAttribute('data-sticky-bar', 'true');
    pixel('ViewContent', {
      content_ids: [(p.colors[0] || {}).sku], content_type: 'product', content_name: p.title,
      content_category: p.fabric, value: p.price / 100, currency: 'PKR'
    });
    renderSwatches(); renderGallery(); renderInfo();

    // warm every colour's images once the page is idle
    var warm = function () { p.colors.forEach(function (c) { (c.images || []).forEach(function (s) { var i = new Image(); i.decoding = 'async'; i.src = sized(s, 1400); }); }); };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2000 }); else setTimeout(warm, 800);
  }

  /* ---------- accordions, tabs, faqs ---------- */
  function initAccordions() {
    var accs = $$('[data-ko-acc]');
    accs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        accs.forEach(function (o) {
          o.setAttribute('aria-expanded', 'false');
          show(o.parentNode.querySelector('[data-ko-acc-body]'), false);
          var ic = o.querySelector('svg'); if (ic) ic.classList.remove('rotate-180');
        });
        if (!open) {
          btn.setAttribute('aria-expanded', 'true');
          show(btn.parentNode.querySelector('[data-ko-acc-body]'), true);
          var ic = btn.querySelector('svg'); if (ic) ic.classList.add('rotate-180');
        }
      });
    });
    var faqs = $$('[data-ko-faq]');
    faqs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        faqs.forEach(function (o) {
          o.setAttribute('aria-expanded', 'false');
          show(o.parentNode.querySelector('[data-ko-faq-body]'), false);
          var ic = o.querySelector('svg'); if (ic) ic.classList.remove('rotate-180');
        });
        if (!open) {
          btn.setAttribute('aria-expanded', 'true');
          show(btn.parentNode.querySelector('[data-ko-faq-body]'), true);
          var ic = btn.querySelector('svg'); if (ic) ic.classList.add('rotate-180');
        }
      });
    });
  }

  function initTabs() {
    $$('[data-ko-tabs]').forEach(function (wrap) {
      var tabs = $$('[data-ko-tab]', wrap);
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          var id = t.getAttribute('data-ko-tab');
          tabs.forEach(function (o) {
            var on = o === t;
            o.setAttribute('aria-selected', on ? 'true' : 'false');
            o.className = 'px-5 py-2.5 text-[13px] border transition-colors ' + (on ? 'bg-ink text-background border-ink' : 'border-hairline text-ink/75 hover:border-ink');
          });
          $$('[data-ko-tab-panel]', wrap).forEach(function (pn) { pn.hidden = pn.getAttribute('data-ko-tab-panel') !== id; });
        });
      });
    });
  }

  /* ---------- shop filters ---------- */
  var COLOR_FAMILIES = [
    { id: 'black', label: 'Black', hex: '#0f0f10', match: /black|onyx|espresso$/ },
    { id: 'grey', label: 'Grey', hex: '#8a8a85', match: /grey|gray|slate|charcoal|cement/ },
    { id: 'white', label: 'White & Cream', hex: '#efe9dc', match: /white|cream|ivory|butter/ },
    { id: 'beige', label: 'Beige & Wheat', hex: '#d5c193', match: /wheat|khaki|sand|taupe|beige/ },
    { id: 'brown', label: 'Brown', hex: '#6a4a34', match: /walnut|brown|espresso|clay|tan/ },
    { id: 'green', label: 'Green', hex: '#5b7a4a', match: /green|olive|sage|pistachio|seafoam|moss|teal/ },
    { id: 'blue', label: 'Blue', hex: '#1e2a44', match: /blue|navy|sky|cobalt|aqua/ },
    { id: 'maroon', label: 'Maroon', hex: '#5a2027', match: /maroon|wine|burgundy|red/ }
  ];
  function familiesFor(id) { return COLOR_FAMILIES.filter(function (f) { return f.match.test(id); }).map(function (f) { return f.id; }); }

  function initShop() {
    var shop = $('[data-ko-shop]');
    if (!shop) return;
    var grid = $('[data-ko-grid]', shop);
    var items = $$('[data-ko-item]', grid);
    var state = { fabric: [], season: [], colors: [], price: 1200000, stock: false, sort: 'featured' };
    items.forEach(function (it) {
      var p = parseJSON($('[data-ko-card-json]', it));
      it._fam = [];
      it._anyAvail = false;
      if (p) p.colors.forEach(function (c) {
        familiesFor(c.id).forEach(function (f) { if (it._fam.indexOf(f) === -1) it._fam.push(f); });
        if (c.available) it._anyAvail = true;
      });
    });
    var present = {};
    items.forEach(function (it) { it._fam.forEach(function (f) { present[f] = true; }); });
    var fams = COLOR_FAMILIES.filter(function (f) { return present[f.id]; });

    var drawer = $('[data-ko-filter-drawer]');
    var containers = [shop, drawer].filter(Boolean);

    containers.forEach(function (c) {
      $$('[data-ko-color-families]', c).forEach(function (box) {
        box.innerHTML = '';
        fams.forEach(function (f) {
          var b = document.createElement('button');
          b.type = 'button'; b.setAttribute('aria-label', f.label); b.title = f.label;
          b.setAttribute('data-ko-family', f.id);
          b.style.background = f.hex;
          b.addEventListener('click', function () {
            var i = state.colors.indexOf(f.id);
            if (i > -1) state.colors.splice(i, 1); else state.colors.push(f.id);
            apply();
          });
          box.appendChild(b);
        });
      });
      $$('[data-ko-filter]', c).forEach(function (inp) {
        inp.addEventListener('change', function () {
          var k = inp.getAttribute('data-ko-filter');
          if (k === 'fabric' || k === 'season') {
            var arr = state[k], i = arr.indexOf(inp.value);
            if (inp.checked && i === -1) arr.push(inp.value);
            if (!inp.checked && i > -1) arr.splice(i, 1);
          } else if (k === 'stock') state.stock = inp.checked;
          apply();
        });
        if (inp.getAttribute('data-ko-filter') === 'price') {
          inp.addEventListener('input', function () { state.price = parseInt(inp.value, 10) * 100; apply(); });
        }
      });
    });
    var sortSel = $('[data-ko-sort]', shop);
    if (sortSel) sortSel.addEventListener('change', function () { state.sort = sortSel.value; apply(); });

    if (drawer) {
      $$('[data-ko-filter-open]').forEach(function (b) { b.addEventListener('click', function () { drawer.hidden = false; }); });
      $$('[data-ko-filter-close]', drawer).forEach(function (b) { b.addEventListener('click', function () { drawer.hidden = true; }); });
    }

    var pills = $('[data-ko-pills]', shop);
    var pillTpl = $('[data-ko-pill-template]');

    function syncControls() {
      containers.forEach(function (c) {
        $$('[data-ko-filter]', c).forEach(function (inp) {
          var k = inp.getAttribute('data-ko-filter');
          if (k === 'fabric' || k === 'season') inp.checked = state[k].indexOf(inp.value) > -1;
          if (k === 'stock') inp.checked = state.stock;
          if (k === 'price') inp.value = state.price / 100;
          var box = inp.parentNode.querySelector('[data-ko-box]');
          if (box) { box.classList.toggle('is-on', inp.checked); box.classList.toggle('border-stone', !inp.checked); }
        });
        $$('[data-ko-price-label]', c).forEach(function (l) { l.textContent = (state.price / 100).toLocaleString(); });
        $$('[data-ko-family]', c).forEach(function (b) {
          var on = state.colors.indexOf(b.getAttribute('data-ko-family')) > -1;
          b.className = 'w-7 h-7 border transition-all ' + (on ? 'border-ink ring-1 ring-ink ring-offset-2 ring-offset-background' : 'border-hairline hover:border-ink');
        });
      });
    }

    function apply() {
      var shown = items.filter(function (it) {
        if (state.fabric.length && state.fabric.indexOf(it.getAttribute('data-fabric')) === -1) return false;
        if (state.season.length && state.season.indexOf(it.getAttribute('data-season')) === -1) return false;
        if (state.colors.length && !it._fam.some(function (f) { return state.colors.indexOf(f) > -1; })) return false;
        if (parseInt(it.getAttribute('data-price'), 10) > state.price) return false;
        if (state.stock && !it._anyAvail) return false;
        return true;
      });
      var order = items.slice();
      var idx = function (it) { return parseInt(it.getAttribute('data-index'), 10); };
      var price = function (it) { return parseInt(it.getAttribute('data-price'), 10); };
      if (state.sort === 'featured') order.sort(function (a, b) { return idx(a) - idx(b); });
      if (state.sort === 'newest') order.sort(function (a, b) { return (b.getAttribute('data-new') === '1') - (a.getAttribute('data-new') === '1') || idx(a) - idx(b); });
      if (state.sort === 'price-asc') order.sort(function (a, b) { return price(a) - price(b) || idx(a) - idx(b); });
      if (state.sort === 'price-desc') order.sort(function (a, b) { return price(b) - price(a) || idx(a) - idx(b); });
      order.forEach(function (it) { grid.appendChild(it); it.hidden = shown.indexOf(it) === -1; });
      $$('[data-ko-shop-count]').forEach(function (el) { el.textContent = shown.length; });
      show($('[data-ko-empty]', shop), shown.length === 0);
      show($('[data-ko-loadmore-wrap]', shop), shown.length > 0);
      // pills
      if (pills && pillTpl) {
        pills.innerHTML = '';
        var list = [];
        state.fabric.forEach(function (f) { list.push([f, function () { state.fabric.splice(state.fabric.indexOf(f), 1); }]); });
        state.season.forEach(function (s) { list.push([s, function () { state.season.splice(state.season.indexOf(s), 1); }]); });
        state.colors.forEach(function (cid) {
          var fam = COLOR_FAMILIES.filter(function (f) { return f.id === cid; })[0];
          list.push([fam ? fam.label : cid, function () { state.colors.splice(state.colors.indexOf(cid), 1); }]);
        });
        list.forEach(function (pl) {
          var node = pillTpl.content.firstElementChild.cloneNode(true);
          node.querySelector('span').textContent = pl[0];
          node.addEventListener('click', function () { pl[1](); apply(); });
          pills.appendChild(node);
        });
        pills.hidden = list.length === 0;
      }
      syncControls();
    }
    apply();
  }

  /* ---------- boot ---------- */
  function boot() {
    initHeader();
    initMenu();
    initSearch();
    initCart();
    QuickView.init();
    initCards(document);
    initProduct();
    initTabs();
    initAccordions();
    initShop();
    initReveal(document);
    initScrollEffects();
    bindUpsell(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { initCards(e.target); initReveal(e.target); });
})();
